import { useEffect, useMemo, useRef, useState } from "react"

import { Checkbox, Dropdown, Flex, Typography } from "antd"
import dayjs from "dayjs"
import { useTranslation } from "react-i18next"

import Button from "../../../../../../Components/Button"
import DropdownBlockCheckbox from "../../../../../../Components/Filters/DropdownBlockCheckbox"
import DropdownBlockNumber from "../../../../../../Components/Filters/DropdownBlockNumber"
import DropdownBlockTags from "../../../../../../Components/Filters/DropdownBlockTags"
import DropdownDateBlock from "../../../../../../Components/Filters/DropdownDateBlock/DropdownDateBlock"
import InputComponent from "../../../../../../Components/Input"
import Icon from "../../../../../../Icon"
import {
  CARD_TYPE_DATA,
  COLUMN_ICONS,
  COUNTRY_DATA,
  CURRENCY_DATA,
  DATE_PARAMS,
  FLAG_REASON_DATA,
  isFiltersDirty,
  ISSUER_BANK_DATA,
  ISSUER_DATA,
  PROCESSOR_DATA,
  REASON_CODE_DATA,
  STAGE_DATA,
  SUBMISSION_DATA
} from "../../constants/allPageData"
import { networkGroupFilterData } from "../../constants/categoryGroups"
import { STATUS_DATA, TYPE_DATA } from "../../constants/statusMeta"

const { Text } = Typography

// The /recover/all filter strip: a single horizontally-scrolling row of chips, the
// "+ Add filter" picker that toggles which chips are shown, and Reset. Lifted out of
// AllPage whole — the page now only hands it the filter state and the setters.
const DisputesFilterBar = ({
  filters,
  setFilters,
  visibleFilters,
  setVisibleFilters,
  onResetFilters,
  setPage,
  members,
  addFilterOpen,
  setAddFilterOpen,
  filterSearch,
  setFilterSearch
}) => {
  const { t } = useTranslation()
  const filtersDirty = isFiltersDirty(filters)

  const dropdownValue = (arr) => (!arr || arr.length === 0 ? "all" : arr)

  // One value/handler pair per date chip, resolved through DATE_PARAMS: dispute date,
  // last update, transaction date and Due all behave identically.
  const dateRangeValue = (key) => {
    const { from, to } = DATE_PARAMS[key]
    return filters[from] || filters[to] ? [filters[from] || null, filters[to] || null] : null
  }

  const handleDateRange = (key) => (obj) => {
    const { id, from: fromKey, to: toKey } = DATE_PARAMS[key]
    const value = obj[id]
    // "All time" emits the string "all", not a range: no bounds is exactly what it means.
    if (value && !Array.isArray(value)) {
      setFilters({ [fromKey]: null, [toKey]: null })
      setPage(1)
      return
    }

    const [from, to] = value || [null, null]
    setFilters({
      [fromKey]: from ? (dayjs.isDayjs(from) ? from.startOf("day").toISOString() : from) : null,
      [toKey]: to ? (dayjs.isDayjs(to) ? to.endOf("day").toISOString() : to) : null
    })
    setPage(1)
  }

  // Category filter presents the 4 network dispute categories (Fraud / Authorization
  // / Processing Errors / Consumer Disputes). Selections expand to the underlying
  // fine-grained reason_category slugs when querying (see fetchDisputes + export saga),
  // so the data model and evidence taxonomy are untouched. Finer filtering is via the
  // Reason code filter.
  const REASON_DATA = useMemo(() => networkGroupFilterData(t), [t])

  // Assignee filter options: the business's invited team members plus an
  // "Unassigned" bucket. Built from the same `members` list as the Assignee column.
  const ASSIGNEE_DATA = useMemo(
    () => [
      // No "Unassigned" bucket: every dispute always has an owner, so it would
      // always return zero rows.
      ...members.map((m) => ({ id: String(m.id), name: m.name }))
    ],
    [members, t]
  )

  // Filter registry: every available filter + how to render it, one chip per table
  // column and named after it. DEFAULT_VISIBLE_FILTERS (reducer.js) picks the five
  // shown by default; the rest are added via "+ Add filter" (Alerts-style).
  const FILTERS = useMemo(
    () => [
      {
        key: "status",
        id: "dispute_state",
        label: "Status",
        icon: "pulse",
        data: STATUS_DATA,
        allText: "All statuses",
        sortDisabled: true
      },
      {
        key: "dispute_type",
        label: "Type",
        icon: "type",
        data: TYPE_DATA,
        allText: "All types",
        sortDisabled: true
      },
      // Network lifecycle stage, the Stage column's own chip.
      {
        key: "cycle_stage",
        label: "Stage",
        icon: COLUMN_ICONS.cycle_stage,
        data: STAGE_DATA,
        allText: "All stages",
        sortDisabled: true
      },
      // Submission event type, the same buckets the Submission column renders.
      {
        key: "submission",
        label: "Submission",
        icon: "paper_plane",
        data: SUBMISSION_DATA,
        allText: "All events",
        sortDisabled: true
      },
      {
        key: "reason_category",
        label: "Category",
        icon: "square_lines",
        data: REASON_DATA,
        allText: "All categories"
      },
      {
        key: "issuer",
        label: "Card network",
        icon: "card",
        data: ISSUER_DATA,
        allText: "All networks"
      },
      {
        key: "issuing_bank",
        label: "Issuer",
        icon: "bank",
        data: ISSUER_BANK_DATA,
        allText: "All issuers"
      },
      { key: "customer", label: "Customer full name", icon: "user", type: "tags" },
      // Free-text email search, the Customer email column's chip.
      {
        key: "customer_email",
        label: "Customer email",
        icon: COLUMN_ICONS.customer_email,
        type: "tags"
      },
      {
        key: "assignee",
        label: "Assignee",
        icon: "two_users",
        data: ASSIGNEE_DATA,
        allText: "All assignees"
      },
      // Response-deadline range, the Due column's chip.
      { key: "due_by", label: "Due", icon: COLUMN_ICONS.due_by, type: "date" },
      { key: "amount", label: "Amount", icon: "money", type: "number" },
      // Enrichment score as a percentage range, reusing the same number block (and its
      // Apply / Cancel) that Amount uses. The v2 "Low enrichment" card is a preset of this
      // same filter rather than a separate param, so the two can never disagree.
      { key: "enrichment", label: "Enrichment", icon: "sparkles_soft", type: "number" },
      { key: "date", label: "Dispute date", icon: "calendar_days", type: "date" },
      { key: "updated", label: "Last update", icon: "clock", type: "date" },
      // Date of the disputed transaction, not of the dispute itself.
      {
        key: "transaction_date",
        label: "Transaction date",
        icon: COLUMN_ICONS.transaction_date,
        type: "date"
      },
      {
        key: "card_type",
        label: "Card type",
        icon: "card",
        data: CARD_TYPE_DATA,
        allText: "All card types"
      },
      {
        key: "currency",
        label: "Currency",
        icon: "money",
        data: CURRENCY_DATA,
        allText: "All currencies"
      },
      {
        key: "country",
        label: "Country",
        icon: "globus",
        data: COUNTRY_DATA,
        allText: "All countries"
      },
      {
        key: "processor",
        label: "Payment processor",
        icon: "processor",
        data: PROCESSOR_DATA,
        allText: "All processors"
      },
      { key: "card_last4", label: "Card last 4", icon: "card", type: "tags" },
      { key: "processor_dispute_id", label: "Dispute ID", icon: "hashtag", type: "tags" },
      // A dropdown of the actual codes (same filter type as Submission), not free text.
      {
        key: "reason_code",
        label: "Reason code",
        icon: "hashtag",
        data: REASON_CODE_DATA,
        allText: "All codes",
        sortDisabled: true
      },
      // Why a dispute is held for manual review, the Flag column's chip.
      {
        key: "flag_reason",
        label: "Flag",
        icon: COLUMN_ICONS.flag_reason,
        data: FLAG_REASON_DATA,
        allText: "All flags",
        sortDisabled: true
      }
    ],
    [REASON_DATA, ASSIGNEE_DATA, t]
  )

  const toggleFilterVisible = (key) => {
    if (visibleFilters.includes(key)) {
      // Hiding a filter clears its value too (matches Alerts).
      if (DATE_PARAMS[key])
        setFilters({ [DATE_PARAMS[key].from]: null, [DATE_PARAMS[key].to]: null })
      else if (key === "amount") setFilters({ amount: null })
      else setFilters({ [key]: [] })
      setVisibleFilters(visibleFilters.filter((k) => k !== key))
    } else {
      setVisibleFilters([...visibleFilters, key])
      // Bring the newly added chip into view (the strip is a single horizontally
      // scrolling row). Deferred so the chip is in the DOM before we scroll. This is
      // the ONLY place we scroll the strip, so it never scrolls on mount/refresh.
      setTimeout(() => {
        filtersRef.current?.scrollBy({ left: 2000 })
      }, 50)
    }
    setPage(1)
  }

  // Single-row, horizontally-scrolling filter strip with an overflow arrow,
  // copied from the Alerts Filters component (src/Components/Filters): instead of
  // wrapping onto new rows, the chips stay on one line and a chevron button
  // appears to scroll them when they overflow.
  const filtersRef = useRef(null)
  const [showScrollButtons, setShowScrollButtons] = useState(false)
  const [canScrollLeft, setCanScrollLeft] = useState(false)

  const checkOverflow = () => {
    const el = filtersRef.current
    if (!el) return
    setShowScrollButtons(el.scrollWidth > el.clientWidth)
  }

  const checkScrollPosition = () => {
    const el = filtersRef.current
    if (!el) return
    setCanScrollLeft(el.scrollLeft > 0)
  }

  const handleScroll = () => {
    const el = filtersRef.current
    if (!el) return
    el.scrollBy({ left: canScrollLeft ? -2000 : 2000, behavior: "smooth" })
  }

  useEffect(() => {
    const el = filtersRef.current
    if (!el) return
    checkOverflow()
    checkScrollPosition()
    el.addEventListener("scroll", checkScrollPosition)
    window.addEventListener("resize", checkOverflow)
    return () => {
      el.removeEventListener("scroll", checkScrollPosition)
      window.removeEventListener("resize", checkOverflow)
    }
  }, [])

  // Re-measure overflow when the chip set changes (add/remove). Does NOT scroll, so the
  // strip stays at its natural left position on mount/refresh; scrolling a newly added
  // chip into view is handled in toggleFilterVisible (the only explicit user-add path).
  useEffect(() => {
    const timer = setTimeout(checkOverflow, 200)
    return () => clearTimeout(timer)
  }, [visibleFilters])

  // Render one filter chip (checkbox-dropdown, free-text tags, or date range).
  const renderFilter = (f) => {
    if (f.type === "date") {
      // Every date chip goes through DATE_PARAMS: its own dropdown id and from/to pair.
      return (
        <DropdownDateBlock
          key={f.key}
          id={DATE_PARAMS[f.key].id}
          label={t(f.label)}
          iconType={f.icon}
          dropdownFilters={dateRangeValue(f.key)}
          setDropdownFilters={handleDateRange(f.key)}
        />
      )
    }
    if (f.type === "tags") {
      // Free-text customer search: name or email, comma-separated (matches Alerts).
      return (
        <DropdownBlockTags
          key={f.key}
          id={f.key}
          label={t(f.label)}
          iconType={f.icon}
          searchPlaceholder={t("Separate by comma")}
          dropdownFilters={filters[f.key] || []}
          setDropdownFilters={(obj) => {
            setFilters({ [f.key]: Array.isArray(obj[f.key]) ? obj[f.key] : [] })
            setPage(1)
          }}
        />
      )
    }
    if (f.type === "number") {
      // Amount condition (equals / greater / less / between). Stored as the
      // {min,max,type} shape DropdownBlockNumber emits; mapped to params on fetch.
      return (
        <DropdownBlockNumber
          key={f.key}
          id={f.key}
          label={t(f.label)}
          iconType={f.icon}
          searchPlaceholder=""
          min={0}
          dropdownFilters={filters[f.key] || {}}
          setDropdownFilters={(obj) => {
            setFilters({ [f.key]: obj[f.key] })
            setPage(1)
          }}
        />
      )
    }
    const ddId = f.id || f.key
    return (
      <DropdownBlockCheckbox
        key={f.key}
        id={ddId}
        label={t(f.label)}
        iconType={f.icon}
        allBtnText={t(f.allText)}
        sortDisabled={f.sortDisabled}
        data={f.data}
        dropdownFilters={dropdownValue(filters[f.key])}
        setDropdownFilters={(obj) => {
          const v = obj[ddId]
          let next
          if (v === "all" || v == null) next = []
          else if (Array.isArray(v)) next = v
          else next = [v]
          setFilters({ [f.key]: next })
          setPage(1)
        }}
      />
    )
  }

  return (
    <Flex align="center" className="alert-filters-container">
      <Flex ref={filtersRef} gap={12} align="center" className="alert-filters">
        {/* Visible filters, in registry order. */}
        {FILTERS.filter((f) => visibleFilters.includes(f.key)).map(renderFilter)}

        {/* + Add filter: toggle any filter on/off. Mirrors the Alerts add-filter
          dropdown exactly (searchable list of checkbox + icon + label rows). */}
        <Dropdown
          getPopupContainer={() => document.getElementById("main-layout")}
          overlayStyle={{ width: 268 }}
          trigger={["click"]}
          open={addFilterOpen}
          onOpenChange={(open) => {
            setAddFilterOpen(open)
            if (!open) setFilterSearch("")
          }}
          placement="bottomLeft"
          menu={{
            items: FILTERS.filter(
              (f) => !filterSearch || t(f.label).toLowerCase().includes(filterSearch.toLowerCase())
            ).map((f) => ({
              key: f.key,
              label: (
                <Flex align="center" gap={8}>
                  <Checkbox checked={visibleFilters.includes(f.key)} onChange={() => {}} />
                  {f.icon ? (
                    <Icon type={f.icon} style={{ fontSize: 20, lineHeight: 1, margin: 0 }} />
                  ) : null}
                  <Text>{t(f.label)}</Text>
                </Flex>
              )
            })),
            onClick: ({ key }) => toggleFilterVisible(key)
          }}
          dropdownRender={(menu) => (
            <div className="dropdown-database-menu add-filters">
              <div className="dropdown-search-wrapper">
                <InputComponent
                  className="dropdown-search"
                  prefix={<Icon type="search" />}
                  placeholder={`${t("Search")}...`}
                  value={filterSearch}
                  onChange={(e) => setFilterSearch(e.target.value)}
                  onClick={(e) => e.stopPropagation()}
                />
              </div>
              {menu}
            </div>
          )}
        >
          <div className="edit-columns-trigger" onClick={(e) => e.stopPropagation()}>
            <Button
              type="text"
              className="flex center"
              icon={<Icon type="plus" style={{ fontSize: 20 }} />}
            >
              {t("Add filter")}
            </Button>
          </div>
        </Dropdown>

        {filtersDirty ? (
          <Button type="text" className="flex" text={t("Reset filters")} onClick={onResetFilters} />
        ) : null}
      </Flex>

      {showScrollButtons ? (
        <Button
          icon={<Icon type={canScrollLeft ? "chevron_left_small" : "chevron_right_small"} />}
          onClick={handleScroll}
          className="flex center scroll-btn-right"
        />
      ) : null}
    </Flex>
  )
}

export default DisputesFilterBar
